/**
 * What a lot's date is (B1b T3 of docs/plans/2026-10-18_actionable-platform.md; org_settings.lot_date_kind, migration
 * 0249): a plant that books its scrap when it pours dates its lots by the pour, one that books at final inspection by
 * the inspection, one that books at dispatch by the shipment. The briefs carry the kind where it is not the pour's
 * (lot_dates: inspected or shipped), and every sentence that dates a lot reads it here. The default (absent, or poured)
 * keeps the neutral "booked" the briefs always said, so an org that has not set the kind is never told its lots were
 * poured on a day that may be its inspection day; only inspected and shipped get words of their own. Pure.
 */

/** The kinds of a lot's date. */
export type LotDateKind = "poured" | "inspected" | "shipped";

/** A lot's date in words: what the castings were on that day, and what their scrap was. */
export interface LotDateWords {
  /** "booked", "inspected", "booked at shipping": "the castings inspected from 1 Jan to 30 Jun". */
  dated: string;
  /** "scrapped", "scrapped at inspection", "scrapped before shipping". */
  scrapped: string;
}

const WORDS: Record<LotDateKind, LotDateWords> = {
  // The default is worded as the briefs always worded it: neutral, since the kind may simply not be set yet.
  poured: { dated: "booked", scrapped: "scrapped" },
  inspected: { dated: "inspected", scrapped: "scrapped at inspection" },
  shipped: { dated: "booked at shipping", scrapped: "scrapped before shipping" },
};

/** A lot's date in words for a brief's lot_dates (absent, or anything else, is the default: "booked"). */
export function lotDateWords(kind: string | null | undefined): LotDateWords {
  return WORDS[(kind ?? "poured") as LotDateKind] ?? WORDS.poured;
}

/** What a brief says of its lots' dates when they are not the pour's; "" when they are. */
export function lotDatesNote(kind: string | null | undefined): string {
  switch (kind) {
    case "inspected":
      return "The plant dates its lots by the day they were inspected, not the day they were poured.";
    case "shipped":
      return "The plant dates its lots by the day they were booked at shipping, not the day they were poured.";
  }
  return "";
}

/** The kinds as Settings offers them, the default first, each with what it means. */
export const LOT_DATE_OPTIONS: readonly { value: LotDateKind; label: string; help: string }[] = [
  { value: "poured", label: "Poured", help: "A lot's date is the day its castings were poured." },
  {
    value: "inspected",
    label: "Inspected",
    help: "A lot's date is the day its castings were inspected, often days after the pour.",
  },
  { value: "shipped", label: "Shipped", help: "A lot's date is the day its castings were booked at shipping." },
];
